import { phpJsonTruthy } from '../compile/php-json-values.js';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers.js';
import { storedStatusRows } from '../compile/creature-status-reader.js';
import { statusItemId } from '../statuses/expiry.js';

type Row = Record<string, unknown>;
export interface ReductionReaders {
    value: (row: Row, key: string) => unknown;
    entries?: (row: Row, key: string) => unknown[];
    statuses?: (row: Row) => readonly Row[];
    definition?: (id: string) => Row | null;
    resolveItem?: (entry: unknown) => Row | null;
}
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Held own layers, active permanent passives, and item-as-status attachments share one reader walk.
 * Timed passives apply through their held status, never through an inactive bucket copy. */
function protectedResource(holder: Row, layer: string, read: ReductionReaders): boolean {
    const seen = [new Set<Row>(), new Set<Row>()];
    const resolve = (entry: unknown): Row | null => {
        const row = record(entry), nested = record(row?.item_arr);
        if (row?.item_layer_types) return row;
        if (nested?.item_layer_types) return nested;
        const id = String(row?.ability_id ?? readLayerListEntryUuid(entry) ?? '').toUpperCase();
        return read.definition?.(id) ?? read.resolveItem?.(entry) ?? nested ?? row;
    };
    const carries = (row: Row, held: boolean): boolean => {
        const visited = seen[held ? 1 : 0]!;
        if (visited.has(row)) return false;
        visited.add(row);
        if (phpJsonTruthy(read.value(row, layer))) return true;
        for (const entry of read.entries?.(row, 'passive_ability') ?? []) {
            const state = record(entry);
            if (state && 'passive_conditions_active' in state && !phpJsonTruthy(state.passive_conditions_active)) continue;
            const definition = resolve(entry);
            if (!definition || (!held && phpJsonTruthy(read.value(definition, 'duration_int')))) continue;
            if (carries(definition, held)) return true;
        }
        return false;
    };
    if (carries(holder, false)) return true;
    for (const status of read.statuses?.(holder) ?? storedStatusRows(holder, 'target')) {
        if (phpJsonTruthy(status.status_deleted) || phpJsonTruthy(status.status_inactive)) continue;
        const definition = read.definition?.(statusItemId(status)) ?? read.resolveItem?.(statusItemId(status)) ?? record(status.status_arr);
        if (definition && carries(definition, true)) return true;
    }
    return false;
}

/** Every mana debit/drain and affordability check uses this guard. Gains pass through unchanged. */
export function manaReductionDelta(holder: Row, delta: number, read: ReductionReaders): number {
    return delta < 0 && protectedResource(holder, 'mana_cannot_be_reduced', read) ? 0 : delta;
}

/** The sheet and engine share a finite investment budget even when no mana will be spent. */
export function manaInvestmentBudget(holder: Row, current: number, read: ReductionReaders): number {
    return manaReductionDelta(holder, -1, read) === 0
        ? Math.max(current, Number(read.value(holder, 'spirit')) || 0) : current;
}

/** Every charge debit/removal and affordability check uses this guard. An inventory pool is protected by either its user or
 * the inventory row itself; creature ability pools omit item. Recharge passes through unchanged. */
export function chargeReductionDelta(holder: Row, delta: number, read: ReductionReaders, item?: Row | null): number {
    return delta < 0 && (protectedResource(holder, 'charges_cannot_be_reduced', read)
        || !!item && protectedResource(item, 'charges_cannot_be_reduced', read)) ? 0 : delta;
}

/** The sheet and engine share a finite creature ability charge investment budget even when no charge will be spent: a protected holder may invest
 * up to the higher of current and maximum charges; an ordinary holder invests what the pool currently holds. The charges twin
 * of manaInvestmentBudget — the ONE place that rule is written (sheet adapter: ability-button.ts sheetChargeBudget). */
export function chargeInvestmentBudget(holder: Row, current: number, max: number, read: ReductionReaders): number {
    return chargeReductionDelta(holder, -1, read) === 0 ? Math.max(current, max) : current;
}
